/**
 * Secure Access → Activity — the user/device explorer (design 19 §7.5,
 * VSNP-1222).
 *
 * The R3 surface: for a user or a device and a time range, which gateway they
 * connected through, from which registered device, which apps and hosts they
 * reached, how much and for how long.
 *
 * Two stores, deliberately (design §4.2):
 *   - the SESSION list is `access_sessions` in MySQL — "who connected, when,
 *     from where, how much";
 *   - the DRILL-DOWN is the audit stream in Loki — "what they reached inside
 *     that session", one line per flow.
 * Neither can answer the other's question at a sane cost, which is why the page
 * is built as a list plus a detail rather than as one table.
 *
 * Filters live in the URL so `Devices → Activity` is a link, and so a support
 * ticket can carry the exact view someone was looking at.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';
import ZtnaChip from '../../components/access/ZtnaChip';
import { SessionsTable } from '../../components/observability/SessionsTable';
import { AuditEventsTable } from '../../components/observability/AuditEventsTable';
import { TimeRange } from '../../components/observability/TimeRange';
import { UserCell } from '../../components/observability/MetricCells';
import accessService, { type AccessGateway } from '../../services/access.service';
import accessObservabilityService, {
  type AccessActivityRow,
  type AccessSessionRow,
  type AuditEvent,
  type SessionAppRollup,
} from '../../services/accessObservability.service';
import { fmtBytes, fmtDuration } from '../../utils/obsChart';
import { DEFAULT_TIME_RANGE, resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';

type GroupBy = 'user' | 'device' | 'app' | 'host' | 'gateway';
type RankBy = 'requests' | 'bytes' | 'denies' | 'sessions';

const AccessActivity = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = searchParams.get('user') ?? '';
  const device = searchParams.get('device') ?? '';
  const app = searchParams.get('app') ?? '';
  const gatewayId = searchParams.get('gateway') ?? '';
  const action = (searchParams.get('action') ?? '') as '' | 'allow' | 'deny' | 'error' | 'abandon';

  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);
  const [selected, setSelected] = useState<AccessSessionRow | null>(null);
  const [flowEvents, setFlowEvents] = useState<AuditEvent[]>([]);
  const [gateways, setGateways] = useState<AccessGateway[]>([]);
  const [rankRows, setRankRows] = useState<AccessActivityRow[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy>('app');
  const [rankBy, setRankBy] = useState<RankBy>('requests');
  const [deviceOnly, setDeviceOnly] = useState<Set<string>>(new Set());
  const [rankLoading, setRankLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setSelected(null);
  };

  useEffect(() => {
    // The gateway filter's options. SuperAdmins in All-Tenants mode never reach
    // this page (TenantGuard), so the list is always tenant-scoped.
    accessService
      .listGateways({ limit: 200 })
      .then((r) => setGateways(r?.items ?? []))
      .catch(() => setGateways([]));
  }, []);

  const loadRankings = useCallback(async () => {
    setRankLoading(true);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      const [activity, sessions] = await Promise.all([
        accessObservabilityService.getActivity(
          {
            // The roll-up's grain is a UTC day (design §4.2 item 4), so the
            // range is truncated to days rather than pretending to be exact.
            from: fromIso.slice(0, 10),
            to: toIso.slice(0, 10),
            user: user || undefined,
            deviceCn: device || undefined,
            app: app || undefined,
            gatewayId: gatewayId || undefined,
            groupBy,
            rankBy,
            limit: 20,
          },
          { showLoading: false },
        ),
        // The roll-up has no `idp_verified` column, so which subjects are really
        // device CNs is answered from the session rows for the same window.
        // Without this a ranking "by user" would print certificate CNs as if
        // they were people.
        accessObservabilityService.listSessions({ from: fromIso, to: toIso, limit: 500 }, { showLoading: false }),
      ]);
      setRankRows(activity?.rows ?? []);
      // A key is device-only when it appears ONLY on unverified sessions. A
      // person who also has verified sessions is a person: their older rows
      // recorded before identity attribution are unverified too, and used to
      // relabel them "device-only (alice)" in the ranking (VSNP-1437).
      const unverified = new Set<string>();
      const verified = new Set<string>();
      for (const s of sessions?.sessions ?? []) {
        if (s.user) (s.idpVerified ? verified : unverified).add(s.user);
      }
      setDeviceOnly(new Set([...unverified].filter((k) => !verified.has(k))));
    } catch {
      setRankRows([]);
      setDeviceOnly(new Set());
    } finally {
      setRankLoading(false);
    }
  }, [range, user, device, app, gatewayId, groupBy, rankBy]);

  useEffect(() => {
    void loadRankings();
  }, [loadRankings]);

  const rankLabel = useCallback(
    (row: AccessActivityRow) => {
      if (groupBy === 'gateway') {
        const gw = gateways.find((g) => g.id === row.key);
        return gw?.name ?? row.key ?? '—';
      }
      if (groupBy === 'user' && deviceOnly.has(row.key)) return `device-only (${row.key})`;
      return row.key || '—';
    },
    [groupBy, gateways, deviceOnly],
  );

  const activeFilters = useMemo(
    () =>
      [
        user && { key: 'user', label: `user: ${user}` },
        device && { key: 'device', label: `device: ${device}` },
        app && { key: 'app', label: `app: ${app}` },
        gatewayId && { key: 'gateway', label: `gateway: ${gateways.find((g) => g.id === gatewayId)?.name ?? gatewayId}` },
        action && { key: 'action', label: `action: ${action}` },
      ].filter(Boolean) as { key: string; label: string }[],
    [user, device, app, gatewayId, action, gateways],
  );

  return (
    <div className="space-y-6" data-testid="access-activity">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Activity</h1>
            <ZtnaChip />
          </div>
          <p className="mt-1 text-gray-600">
            Sessions from the orchestrator's record, and every app and host reached inside one from the audit stream.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRange value={range} onChange={setRange} />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void loadRankings();
              // Keyed remount is how a page-level Refresh reaches the child
              // panels that own their own fetch.
              setRefreshNonce((n) => n + 1);
            }}
          >
            <RefreshCw className={`mr-2 inline h-4 w-4 ${rankLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters (design §7.5) */}
      <div className="card">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <label className="text-xs font-medium text-gray-600">
            User
            <input
              className="input-base mt-1 w-full"
              defaultValue={user}
              placeholder="email, username or IdP sub"
              title="Matches the sessions this person opened, by the identity their token verified — on any device, and never someone else's session on a device they own"
              data-testid="filter-user"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setFilter('user', (e.target as HTMLInputElement).value.trim());
              }}
              onBlur={(e) => setFilter('user', e.target.value.trim())}
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Device (CN)
            <input
              className="input-base mt-1 w-full"
              defaultValue={device}
              placeholder="alice-laptop"
              data-testid="filter-device"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setFilter('device', (e.target as HTMLInputElement).value.trim());
              }}
              onBlur={(e) => setFilter('device', e.target.value.trim())}
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            App
            <input
              className="input-base mt-1 w-full"
              defaultValue={app}
              placeholder="e2e-labnet"
              data-testid="filter-app"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setFilter('app', (e.target as HTMLInputElement).value.trim());
              }}
              onBlur={(e) => setFilter('app', e.target.value.trim())}
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Gateway
            <select
              className="input-base mt-1 w-full"
              value={gatewayId}
              data-testid="filter-gateway"
              onChange={(e) => setFilter('gateway', e.target.value)}
            >
              <option value="">All gateways</option>
              {gateways.map((g) => (
                <option key={g.id} value={g.id}>{g.name ?? g.node_id}</option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-gray-600">
            Action
            <select
              className="input-base mt-1 w-full"
              value={action}
              data-testid="filter-action"
              onChange={(e) => setFilter('action', e.target.value)}
            >
              <option value="">Any</option>
              <option value="allow">allow</option>
              <option value="deny">deny</option>
              <option value="error">error</option>
              {/* flow.abandon (VSNP-1427): held for hostname classification and
                  ended with no decision. Not an `action` in the audit record —
                  it carries none — but the fourth way a flow ends. */}
              <option value="abandon">abandoned (no decision)</option>
            </select>
          </label>
        </div>
        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeFilters.map((f) => (
              <button
                key={f.key}
                type="button"
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700"
                onClick={() => setFilter(f.key, '')}
              >
                {f.label} <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sessions (MySQL) */}
      <SessionsTable
        key={`sessions-${refreshNonce}`}
        range={range}
        user={user || undefined}
        deviceCn={device || undefined}
        gatewayId={gatewayId || undefined}
        onSelect={(s) => setSelected(s)}
        selectedId={selected?.id ?? null}
        title="Sessions"
      />

      {/* Drill-down (Loki) */}
      {selected ? (
        <div className="space-y-3" data-testid="session-drilldown">
          <div className="card">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <h3 className="mr-auto text-sm font-semibold text-gray-900">
                Session <span className="font-mono text-xs text-gray-500">{selected.sessionId.slice(0, 16)}…</span>
              </h3>
              <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-4">
              <div>
                <span className="text-gray-400">User</span>
                <div>
                  <UserCell
                    user={selected.user}
                    idpVerified={selected.idpVerified}
                    userEmail={selected.userEmail}
                    userSub={selected.userSub}
                    preAttribution={!!user && !selected.userSub}
                  />
                </div>
              </div>
              <div>
                <span className="text-gray-400">Device</span>
                <div className="text-gray-800">{selected.deviceName || selected.deviceCn || '—'}</div>
              </div>
              <div>
                <span className="text-gray-400">Gateway / source</span>
                <div className="font-mono text-[11px] text-gray-700">{selected.node} · {selected.srcIp ?? '—'}</div>
              </div>
              <div>
                <span className="text-gray-400">Duration / bytes</span>
                <div className="text-gray-800">
                  {fmtDuration(selected.durationSeconds)} · ↑{fmtBytes(selected.bytesC2s)} ↓{fmtBytes(selected.bytesS2c)}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Posture source</span>
                <div className="text-gray-800">{selected.postureSource ?? '—'}</div>
              </div>
              <div>
                <span className="text-gray-400">Flows</span>
                <div className="text-gray-800">{selected.flows}</div>
              </div>
              <div>
                <span className="text-gray-400">Close reason</span>
                <div className="text-gray-800">{selected.rejectReason ?? selected.closeReason ?? 'open'}</div>
              </div>
              <div>
                <span className="text-gray-400">Serial</span>
                <div className="font-mono text-[11px] text-gray-600">{selected.serial ?? '—'}</div>
              </div>
            </div>
          </div>

          {/* What the session touched, grouped by HOST, from the same events
              the list below shows (design §9b). Grouped here rather than
              server-side because the events are already being fetched for the
              list: a second query would ask the log store the same question
              twice to arrange the answer differently. */}
          <HostBreakdown apps={selected.apps} events={flowEvents} />

          {/* Apps, hosts, allow/deny and bytes inside this session. `eventClass`
              is 'flow', which is the design §7.5 drill-down verbatim. */}
          <AuditEventsTable
            range={range}
            sessionId={selected.sessionId}
            eventClass="flow"
            app={app || undefined}
            action={action || undefined}
            limit={500}
            title="Flows in this session"
            testId="session-flows"
            onLoaded={setFlowEvents}
          />
        </div>
      ) : (
        <p className="text-sm text-gray-500" data-testid="drilldown-hint">
          Select a session to see the apps and hosts it reached.
        </p>
      )}

      {/* Rankings (access_activity_daily) */}
      <div className="card" data-testid="activity-rankings">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="mr-auto text-sm font-semibold text-gray-900">Rankings</h3>
          <select
            className="input-base w-36 text-xs"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            data-testid="rank-groupby"
            aria-label="Group by"
          >
            <option value="app">by app</option>
            {/* "Top websites", the §9b dimension. Listed second because it is
                the question users actually arrive with — the app name only
                distinguishes registered apps, and breakout traffic is all one
                app. */}
            <option value="host">by host</option>
            <option value="user">by user</option>
            <option value="device">by device</option>
            <option value="gateway">by gateway</option>
          </select>
          <select
            className="input-base w-36 text-xs"
            value={rankBy}
            onChange={(e) => setRankBy(e.target.value as RankBy)}
            data-testid="rank-rankby"
            aria-label="Rank by"
          >
            <option value="requests">by requests</option>
            <option value="bytes">by bytes</option>
            <option value="denies">by denies</option>
            <option value="sessions">by sessions</option>
          </select>
        </div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">{groupBy}</th>
              <th className="py-2 pr-3 text-right">Sessions</th>
              <th className="py-2 pr-3 text-right">Requests</th>
              <th className="py-2 pr-3 text-right">Allows</th>
              <th className="py-2 pr-3 text-right">Denies</th>
              <th className="py-2 pr-3 text-right">Bytes</th>
              <th className="py-2 pr-3">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rankLoading ? (
              <tr><td colSpan={7} className="py-6 text-center text-gray-400">Loading…</td></tr>
            ) : rankRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-6 text-center text-gray-400" data-testid="rankings-empty">
                  No roll-up for this range yet — the daily job aggregates the previous day.
                </td>
              </tr>
            ) : (
              rankRows.map((r) => (
                <tr key={r.key} className="hover:bg-gray-50" data-testid="ranking-row">
                  <td className="py-1.5 pr-3 text-gray-800">{rankLabel(r)}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-700">{r.sessions}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-700">{r.requests}</td>
                  <td className="py-1.5 pr-3 text-right text-success-700">{r.allows}</td>
                  <td className="py-1.5 pr-3 text-right text-danger-700">{r.denies}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-700">{fmtBytes(r.bytesTotal)}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500">{r.lastSeen ? r.lastSeen.replace('T', ' ').slice(0, 19) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * A session's traffic grouped by hostname.
 *
 * Two sources, deliberately, because they fail in opposite directions. The
 * session's own `apps` rollup is complete for the whole session but capped at
 * twenty entries and only as fresh as the last progress record; the flow events
 * are exact and current but only cover the range and limit the list fetched.
 * Preferring the rollup and falling back to the events means a long session
 * still shows what it reached, and a session with no rollup (an older gateway
 * build) is not blank.
 */
const HostBreakdown = ({ apps, events }: { apps: SessionAppRollup[] | null; events: AuditEvent[] }) => {
  const rows = useMemo(() => {
    if (apps && apps.length > 0) {
      return apps
        .filter((a) => a.host || a.app)
        .map((a) => ({
          host: a.host || a.app || '—',
          app: a.host ? a.app : undefined,
          flows: a.flows,
          bytes: a.bytesC2s + a.bytesS2c,
        }));
    }
    const byHost = new Map<string, { host: string; app?: string; flows: number; bytes: number }>();
    for (const e of events) {
      const host = e.host || e.app || e.dest;
      if (!host) continue;
      const row = byHost.get(host) ?? { host, app: e.host ? e.app : undefined, flows: 0, bytes: 0 };
      // Only close events carry bytes; counting them on allow as well would
      // double every total (the same rule the server-side roll-up follows).
      if (e.event === 'flow.close') {
        row.bytes += Number(e.raw?.bytes_c2s ?? 0) + Number(e.raw?.bytes_s2c ?? 0);
      }
      row.flows += 1;
      byHost.set(host, row);
    }
    return [...byHost.values()].sort((a, b) => b.bytes - a.bytes || b.flows - a.flows);
  }, [apps, events]);

  return (
    <div className="card" data-testid="session-hosts">
      <h3 className="mb-2 text-sm font-semibold text-gray-900">
        Hosts reached <span className="ml-1 text-xs font-normal text-gray-400">{rows.length}</span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400" data-testid="session-hosts-empty">
          No host was observed for this session — the gateway saw no SNI, Host header or DNS lookup it could attribute.
        </p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Host</th>
              <th className="py-2 pr-3">App</th>
              <th className="py-2 pr-3 text-right">Flows</th>
              <th className="py-2 pr-3 text-right">Bytes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={`${r.host}-${r.app ?? ''}`} data-testid="session-host-row">
                <td className="py-1.5 pr-3 font-medium text-gray-800">{r.host}</td>
                <td className="py-1.5 pr-3 text-xs text-gray-500">{r.app ?? '—'}</td>
                <td className="py-1.5 pr-3 text-right text-gray-700">{r.flows}</td>
                <td className="py-1.5 pr-3 text-right text-gray-700">{fmtBytes(r.bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AccessActivity;
